import { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  LayoutDashboard,
  Users,
  Bot,
  Brain,
  Zap,
  Settings,
  LogOut,
  Menu,
  X,
  Search,
  Bell,
  Plus,
  CheckCircle,
  AlertCircle,
  Info,
  AlertTriangle,
  Trash2,
  Inbox,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
} from 'lucide-react';
import { useAuthStore } from '../../store/auth';
import { useNotificationStore, type Notification, notify } from '../../store/notifications';
import { useThemeStore } from '../../store/theme';
import { useWorkspaceStore, workspaceColors, type Workspace } from '../../store/workspace';
import { useInboxSidebarStore } from '../../store/inboxSidebar';
import ThemeToggle from '../ThemeToggle';
import InboxSidebarPanel from './InboxSidebarPanel';
import WorkspaceSettingsModal from './WorkspaceSettingsModal';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

// Main navigation items (middle section)
const mainNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Inbox', href: '/dashboard/inbox', icon: Inbox, badge: 0 },
  { name: 'AI Employees', href: '/dashboard/employees', icon: Bot },
  { name: 'Brain AI', href: '/dashboard/brain', icon: Brain },
  { name: 'Automation', href: '/dashboard/automation', icon: Zap },
];

// Notification icon component
const NotificationIcon = ({ type }: { type: Notification['type'] }) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-accent-green" />;
    case 'error':
      return <AlertCircle className="w-5 h-5 text-accent-red" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-accent-orange" />;
    default:
      return <Info className="w-5 h-5 text-accent-cyan" />;
  }
};

// Format relative time
const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

// Tooltip component for icon hover - positioned right next to icon
const Tooltip = ({ children, text, show, persist = false }: { children: React.ReactNode; text: string; show: boolean; persist?: boolean }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPersisted, setIsPersisted] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 72 });
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate position for tooltip
  const updatePosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 8, // 8px gap from the icon
      });
    }
  };

  // Calculate position on mouse enter for immediate positioning
  const handleMouseEnter = () => {
    updatePosition();
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    if (persist) {
      updatePosition();
      setIsPersisted(prev => !prev);
    }
  };

  // Reset persisted state when show becomes false
  useEffect(() => {
    if (!show) {
      setIsPersisted(false);
      setIsHovered(false);
    }
  }, [show]);

  // Also reset on hover state changes when not showing
  useEffect(() => {
    if (!show && (isHovered || isPersisted)) {
      setIsHovered(false);
      setIsPersisted(false);
    }
  }, [show, isHovered, isPersisted]);

  if (!show) return <>{children}</>;

  const shouldShowTooltip = isHovered || isPersisted;

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}
      <AnimatePresence>
        {shouldShowTooltip && tooltipPosition.top > 0 && (
          <motion.div
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -5 }}
            className="fixed px-3 py-1.5 bg-dark-800 text-white text-sm font-medium rounded-lg whitespace-nowrap shadow-lg border border-border pointer-events-none"
            style={{
              zIndex: 9999,
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: 'translateY(-50%)',
            }}
          >
            {text}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent border-r-dark-800" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const {
    notifications,
    unreadCount,
    isOpen: isNotificationOpen,
    togglePanel,
    closePanel,
    markAsRead,
    markAllAsRead,
    removeNotification,
  } = useNotificationStore();

  // Inbox sidebar store
  const {
    togglePanel: toggleInboxPanel,
    badgeCount: inboxBadgeCount,
  } = useInboxSidebarStore();

  // Sidebar collapsed by default
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isMobileWorkspaceOpen, setIsMobileWorkspaceOpen] = useState(false);
  const [isNewWorkspaceModalOpen, setIsNewWorkspaceModalOpen] = useState(false);
  const [isWorkspaceSettingsOpen, setIsWorkspaceSettingsOpen] = useState(false);
  const [settingsWorkspace, setSettingsWorkspace] = useState<Workspace | null>(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceColor, setNewWorkspaceColor] = useState<string>('purple');
  const [newWorkspaceType, setNewWorkspaceType] = useState<'team' | 'personal'>('team');
  const notificationRef = useRef<HTMLDivElement>(null);
  const workspaceDropdownRef = useRef<HTMLDivElement>(null);
  const { token } = useAuthStore();

  // Workspace store
  const {
    workspaces,
    currentWorkspace,
    fetchWorkspaces,
    switchWorkspace,
    createWorkspace,
  } = useWorkspaceStore();

  // Fetch workspaces on mount
  useEffect(() => {
    if (token) {
      fetchWorkspaces(token);
    }
  }, [token, fetchWorkspaces]);

  // Fetch inbox count
  useEffect(() => {
    const fetchInboxCount = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/inbox/count', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json() as { success: boolean; data?: { count: number } };
          if (data.success && data.data) {
            setInboxCount(data.data.count);
          }
        }
      } catch {
        // Silently fail
      }
    };
    fetchInboxCount();
    const interval = setInterval(fetchInboxCount, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Track last seen communication timestamps to detect new messages
  const lastSeenRef = useRef<{ gmail: string; slack: string; discord: string }>({
    gmail: localStorage.getItem('lastSeenGmail') || new Date().toISOString(),
    slack: localStorage.getItem('lastSeenSlack') || new Date().toISOString(),
    discord: localStorage.getItem('lastSeenDiscord') || new Date().toISOString(),
  });

  // Fetch communication notifications (new emails, slack messages, discord messages)
  useEffect(() => {
    const fetchCommunicationNotifications = async () => {
      if (!token) return;

      try {
        const workspaceParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
        const response = await fetch(`/api/communication/summary${workspaceParam}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json() as {
            success: boolean;
            data?: {
              gmail: {
                connected: boolean;
                recentEmails: Array<{
                  id: string;
                  fromName: string;
                  subject: string;
                  timestamp: string;
                }>;
              };
              slack: {
                connected: boolean;
                recentMessages: Array<{
                  channelId: string;
                  channelName: string;
                  userName: string;
                  text: string;
                  timestamp: string;
                }>;
              };
              discord: {
                connected: boolean;
              };
            };
          };

          if (data.success && data.data) {
            const { addNotification } = useNotificationStore.getState();
            const existingNotifications = useNotificationStore.getState().notifications;

            // Check for new Gmail emails
            if (data.data.gmail?.connected && data.data.gmail.recentEmails) {
              for (const email of data.data.gmail.recentEmails) {
                const emailTime = new Date(email.timestamp).getTime();
                const lastSeen = new Date(lastSeenRef.current.gmail).getTime();

                // Check if this is a new email and not already in notifications
                if (emailTime > lastSeen) {
                  const notifId = `gmail_${email.id}`;
                  const alreadyExists = existingNotifications.some(n => n.id === notifId || n.title === `New email from ${email.fromName}`);

                  if (!alreadyExists) {
                    addNotification({
                      type: 'info',
                      title: `New email from ${email.fromName}`,
                      message: email.subject,
                      link: '/dashboard/communication?platform=gmail',
                    });
                  }
                }
              }

              // Update last seen timestamp
              if (data.data.gmail.recentEmails.length > 0) {
                const newestEmail = data.data.gmail.recentEmails[0];
                if (new Date(newestEmail.timestamp) > new Date(lastSeenRef.current.gmail)) {
                  lastSeenRef.current.gmail = newestEmail.timestamp;
                  localStorage.setItem('lastSeenGmail', newestEmail.timestamp);
                }
              }
            }

            // Check for new Slack messages
            if (data.data.slack?.connected && data.data.slack.recentMessages) {
              for (const message of data.data.slack.recentMessages) {
                const msgTime = new Date(message.timestamp).getTime();
                const lastSeen = new Date(lastSeenRef.current.slack).getTime();

                // Check if this is a new message and not already in notifications
                if (msgTime > lastSeen) {
                  const notifId = `slack_${message.channelId}_${message.timestamp}`;
                  const alreadyExists = existingNotifications.some(n => n.id === notifId || (n.title === `New Slack message in #${message.channelName}` && n.message === message.text.substring(0, 100)));

                  if (!alreadyExists) {
                    addNotification({
                      type: 'info',
                      title: `New Slack message in #${message.channelName}`,
                      message: `${message.userName}: ${message.text.substring(0, 100)}`,
                      link: `/dashboard/communication?platform=slack&channel=${message.channelId}`,
                    });
                  }
                }
              }

              // Update last seen timestamp
              if (data.data.slack.recentMessages.length > 0) {
                const newestMsg = data.data.slack.recentMessages[0];
                if (new Date(newestMsg.timestamp) > new Date(lastSeenRef.current.slack)) {
                  lastSeenRef.current.slack = newestMsg.timestamp;
                  localStorage.setItem('lastSeenSlack', newestMsg.timestamp);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch communication notifications:', error);
      }
    };

    // Initial fetch
    fetchCommunicationNotifications();

    // Poll every 2 minutes for new messages
    const interval = setInterval(fetchCommunicationNotifications, 120000);

    return () => clearInterval(interval);
  }, [token, currentWorkspace?.id]);

  // Close workspace dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (workspaceDropdownRef.current && !workspaceDropdownRef.current.contains(event.target as Node)) {
        setIsWorkspaceDropdownOpen(false);
      }
    };

    if (isWorkspaceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isWorkspaceDropdownOpen]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Listen for custom event to open sidebar from chat page
  useEffect(() => {
    const handleOpenSidebar = () => {
      setIsMobileSidebarOpen(true);
    };
    window.addEventListener('openDashboardSidebar', handleOpenSidebar);
    return () => {
      window.removeEventListener('openDashboardSidebar', handleOpenSidebar);
    };
  }, []);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        closePanel();
      }
    };

    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen, closePanel]);

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
      closePanel();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle workspace switch
  const handleSwitchWorkspace = async (workspace: Workspace) => {
    if (token) {
      await switchWorkspace(token, workspace.id);
      setIsWorkspaceDropdownOpen(false);
      // Reload the page to refresh all data for the new workspace
      window.location.reload();
    }
  };

  // Handle create new workspace
  const handleCreateWorkspace = async () => {
    if (!token || !newWorkspaceName.trim()) return;

    const workspace = await createWorkspace(token, {
      name: newWorkspaceName.trim(),
      color: newWorkspaceColor,
      type: newWorkspaceType,
    });

    if (workspace) {
      setIsNewWorkspaceModalOpen(false);
      setNewWorkspaceName('');
      setNewWorkspaceColor('purple');
      setNewWorkspaceType('team');
      // Switch to the new workspace
      await switchWorkspace(token, workspace.id);
      window.location.reload();
    }
  };

  // Get workspace avatar initial and color
  const getWorkspaceAvatar = (workspace: Workspace) => {
    const initial = workspace.name.charAt(0).toUpperCase();
    const bgColor = workspaceColors[workspace.color] || workspaceColors.purple;
    return { initial, bgColor };
  };

  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Check if we're on an employee chat page
  const isEmployeeChatPage = location.pathname.match(/^\/dashboard\/employees\/[^/]+$/);
  // Check if we're on a full-width page (no padding needed)
  const isFullWidthPage = location.pathname === '/dashboard/social-media-manager' ||
                          location.pathname === '/dashboard/communication';

  // Sidebar width based on expanded state
  const sidebarWidth = isSidebarExpanded ? 'w-64' : 'w-[68px]';
  const mainPadding = isSidebarExpanded ? 'lg:pl-64' : 'lg:pl-[68px]';

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Mobile Sidebar Backdrop */}
      <AnimatePresence>
        {isMobileSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Desktop Sidebar - Icon-only collapsible */}
      <aside
        className={`fixed top-0 left-0 h-full ${sidebarWidth} z-50 hidden lg:flex flex-col transition-all duration-300 ${isDark ? 'bg-dark-800' : 'bg-white'}`}
      >
        {/* Top Icon - Workspace Selector */}
        <div className="h-16 flex items-center justify-center relative" ref={workspaceDropdownRef}>
          <Tooltip text={currentWorkspace?.name || 'Workspaces'} show={!isSidebarExpanded && !isWorkspaceDropdownOpen} persist>
            <button
              onClick={() => setIsWorkspaceDropdownOpen(!isWorkspaceDropdownOpen)}
              className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: currentWorkspace ? workspaceColors[currentWorkspace.color] : '#8B5CF6' }}
              >
                <span className="text-lg font-bold text-white">
                  {currentWorkspace?.name?.charAt(0).toUpperCase() || 'W'}
                </span>
              </div>
              {isSidebarExpanded && (
                <span className={`ml-3 text-lg font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {currentWorkspace?.name || 'Workspace'}
                </span>
              )}
            </button>
          </Tooltip>

          {/* Workspace Dropdown */}
          <AnimatePresence>
            {isWorkspaceDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`absolute top-full left-0 mt-2 w-72 rounded-2xl shadow-xl border z-[9999] ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
                style={{ marginLeft: '8px' }}
              >
                {/* Header */}
                <div className={`px-4 py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Workspaces</h3>
                </div>

                {/* Workspace List */}
                <div className="py-2 max-h-64 overflow-y-auto">
                  {workspaces.map((workspace) => {
                    const { initial, bgColor } = getWorkspaceAvatar(workspace);
                    const isActive = currentWorkspace?.id === workspace.id;

                    return (
                      <button
                        key={workspace.id}
                        onClick={() => handleSwitchWorkspace(workspace)}
                        className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                          isActive
                            ? isDark ? 'bg-dark-700' : 'bg-slate-100'
                            : isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: bgColor }}
                        >
                          <span className="text-lg font-bold text-white">{initial}</span>
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            {workspace.name}
                          </p>
                          {workspace.is_default === 1 && (
                            <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>Default</p>
                          )}
                        </div>
                        {isActive && (
                          <Check className={`w-5 h-5 flex-shrink-0 ${isDark ? 'text-accent-cyan' : 'text-blue-500'}`} />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Open workspace settings modal
                            setSettingsWorkspace(workspace);
                            setIsWorkspaceSettingsOpen(true);
                            setIsWorkspaceDropdownOpen(false);
                          }}
                          className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-dark-600' : 'hover:bg-slate-200'}`}
                        >
                          <Settings className="w-4 h-4" />
                        </button>
                      </button>
                    );
                  })}
                </div>

                {/* Add Workspace Button */}
                <div className={`border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
                  <button
                    onClick={() => {
                      setIsWorkspaceDropdownOpen(false);
                      setIsNewWorkspaceModalOpen(true);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-50'}`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                      <Plus className={`w-5 h-5 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`} />
                    </div>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Add workspace</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Main Navigation - Centered vertically */}
        <nav className="flex-1 py-4 flex flex-col items-center justify-center space-y-2">
          {mainNavigation.map((item) => {
            const isActive = location.pathname === item.href ||
              (item.href === '/dashboard/employees' && location.pathname.startsWith('/dashboard/employees'));
            const badgeCount = item.name === 'Inbox' ? (inboxBadgeCount || 0) : (item.badge || 0);

            // Special handling for Inbox - opens sidebar panel instead of navigating
            if (item.name === 'Inbox') {
              return (
                <Tooltip key={item.name} text={item.name} show={!isSidebarExpanded}>
                  <button
                    onClick={toggleInboxPanel}
                    data-inbox-toggle
                    className={`relative flex items-center ${isSidebarExpanded ? 'w-[calc(100%-24px)] px-4' : 'w-12 justify-center'} h-12 rounded-xl transition-all duration-200 ${
                      isDark
                        ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5 flex-shrink-0" />
                    {isSidebarExpanded && (
                      <span className="ml-3 font-medium text-sm">{item.name}</span>
                    )}
                    {badgeCount > 0 && (
                      <span className={`absolute ${isSidebarExpanded ? 'right-3' : '-top-1 -right-1'} min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold bg-accent-red text-white`}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </button>
                </Tooltip>
              );
            }

            return (
              <Tooltip key={item.name} text={item.name} show={!isSidebarExpanded}>
                <Link
                  to={item.href}
                  className={`relative flex items-center ${isSidebarExpanded ? 'w-[calc(100%-24px)] px-4' : 'w-12 justify-center'} h-12 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-accent text-white shadow-glow-sm'
                      : isDark
                        ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                        : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  {isSidebarExpanded && (
                    <span className="ml-3 font-medium text-sm">{item.name}</span>
                  )}
                  {badgeCount > 0 && (
                    <span className={`absolute ${isSidebarExpanded ? 'right-3' : '-top-1 -right-1'} min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-accent-red text-white'
                    }`}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </Link>
              </Tooltip>
            );
          })}
        </nav>

        {/* Bottom Section - Settings & Expand Toggle */}
        <div className="py-4 flex flex-col items-center space-y-2">
          {/* Settings */}
          <Tooltip text="Settings" show={!isSidebarExpanded}>
            <Link
              to="/dashboard/settings"
              className={`relative flex items-center ${isSidebarExpanded ? 'w-[calc(100%-24px)] px-4' : 'w-12 justify-center'} h-12 rounded-xl transition-all duration-200 ${
                location.pathname === '/dashboard/settings'
                  ? 'bg-gradient-accent text-white shadow-glow-sm'
                  : isDark
                    ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <Settings className="w-5 h-5 flex-shrink-0" />
              {isSidebarExpanded && (
                <span className="ml-3 font-medium text-sm">Settings</span>
              )}
            </Link>
          </Tooltip>

          {/* Expand/Collapse Toggle */}
          <button
            onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
            className={`flex items-center ${isSidebarExpanded ? 'w-[calc(100%-24px)] px-4' : 'w-12 justify-center'} h-10 rounded-xl transition-all duration-200 ${isDark ? 'text-text-muted hover:bg-dark-700 hover:text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-900'}`}
          >
            {isSidebarExpanded ? (
              <>
                <ChevronLeft className="w-5 h-5 flex-shrink-0" />
                <span className="ml-3 text-sm">Collapse</span>
              </>
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar - Full sidebar with all items */}
      <aside
        className={`fixed top-0 left-0 h-full w-[280px] border-r z-50 transform transition-transform duration-300 lg:hidden ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`h-14 flex items-center justify-between px-4 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <Link to="/dashboard">
              <img
                src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                alt="SweetPilot"
                className="w-48 object-contain"
              />
            </Link>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`p-1.5 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile Workspace Switcher - Collapsible Dropdown */}
          <div className={`border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
            {/* Dropdown Toggle Button */}
            <button
              onClick={() => setIsMobileWorkspaceOpen(!isMobileWorkspaceOpen)}
              className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-50'}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: currentWorkspace ? workspaceColors[currentWorkspace.color] : '#8B5CF6' }}
                >
                  <span className="text-sm font-bold text-white">
                    {currentWorkspace?.name?.charAt(0).toUpperCase() || 'W'}
                  </span>
                </div>
                <div className="text-left">
                  <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>Workspace</p>
                  <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {currentWorkspace?.name || 'Select Workspace'}
                  </p>
                </div>
              </div>
              <ChevronDown className={`w-5 h-5 transition-transform ${isMobileWorkspaceOpen ? 'rotate-180' : ''} ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
            </button>

            {/* Dropdown Content */}
            <AnimatePresence>
              {isMobileWorkspaceOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className={`px-4 pb-3 space-y-1 ${isDark ? 'bg-dark-900/50' : 'bg-slate-50/50'}`}>
                    {workspaces.map((workspace) => {
                      const { initial, bgColor } = getWorkspaceAvatar(workspace);
                      const isActive = currentWorkspace?.id === workspace.id;

                      return (
                        <button
                          key={workspace.id}
                          onClick={() => {
                            handleSwitchWorkspace(workspace);
                            setIsMobileSidebarOpen(false);
                            setIsMobileWorkspaceOpen(false);
                          }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                            isActive
                              ? isDark ? 'bg-dark-700' : 'bg-slate-100'
                              : isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'
                          }`}
                        >
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: bgColor }}
                          >
                            <span className="text-sm font-bold text-white">{initial}</span>
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {workspace.name}
                            </p>
                          </div>
                          {isActive && (
                            <Check className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-accent-cyan' : 'text-blue-500'}`} />
                          )}
                        </button>
                      );
                    })}
                    {/* Add Workspace Button */}
                    <button
                      onClick={() => {
                        setIsMobileSidebarOpen(false);
                        setIsMobileWorkspaceOpen(false);
                        setIsNewWorkspaceModalOpen(true);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                        <Plus className={`w-4 h-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`} />
                      </div>
                      <span className={`font-medium text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>Add workspace</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {mainNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              const badgeCount = item.name === 'Inbox' ? (inboxBadgeCount || 0) : (item.badge || 0);

              // Special handling for Inbox - opens sidebar panel instead of navigating
              if (item.name === 'Inbox') {
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      setIsMobileSidebarOpen(false);
                      toggleInboxPanel();
                    }}
                    data-inbox-toggle
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 ${
                      isDark
                        ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.name}</span>
                    </div>
                    {badgeCount > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold bg-accent-red text-white">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </button>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-accent text-white shadow-glow-sm'
                      : isDark
                        ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  {badgeCount > 0 && (
                    <span className={`min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-xs font-bold ${
                      isActive ? 'bg-white/20 text-white' : 'bg-accent-red text-white'
                    }`}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Settings Link */}
          <div className={`p-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <Link
              to="/dashboard/settings"
              className={`flex items-center space-x-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                location.pathname === '/dashboard/settings'
                  ? 'bg-gradient-accent text-white shadow-glow-sm'
                  : isDark
                    ? 'text-text-secondary hover:bg-dark-700 hover:text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Settings</span>
            </Link>
          </div>

          {/* User Section */}
          <div className={`p-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-accent flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-white">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {user?.name || 'User'}
                </p>
                <p className={`text-xs truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                  {user?.email || ''}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-accent-red hover:bg-accent-red/10 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={isEmployeeChatPage ? '' : mainPadding}>
        {/* Top Bar */}
        {!isEmployeeChatPage && (
          <header className={`sticky top-0 z-30 h-14 sm:h-16 backdrop-blur-xl border-b ${isDark ? 'bg-dark-900/80 border-border' : 'bg-white/80 border-slate-200'}`}>
            <div className="h-full flex items-center justify-between px-3 sm:px-4 lg:px-8">
              {/* Left Section */}
              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={() => setIsMobileSidebarOpen(true)}
                  className={`lg:hidden p-1.5 sm:p-2 ${isDark ? 'text-text-muted hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                >
                  <Menu className="w-5 h-5" />
                </button>

                {/* Search */}
                <div className="hidden md:block relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                  <input
                    type="text"
                    placeholder="Search employees, tasks..."
                    className={`w-48 lg:w-64 pl-10 pr-4 py-2 border rounded-xl text-sm focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                  />
                </div>
              </div>

              {/* Right Section */}
              <div className="flex items-center space-x-1.5 sm:space-x-3">
                {/* New Chat Button */}
                <Link
                  to="/dashboard/employees"
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 bg-orange-500 hover:bg-orange-600 rounded-full text-white transition-colors shadow-md hover:shadow-lg"
                  title="New Chat"
                >
                  <Plus className="w-5 h-5" />
                </Link>

                {/* Theme Toggle */}
                <ThemeToggle />

                {/* Notifications */}
                <div ref={notificationRef} className="relative">
                  <button
                    onClick={togglePanel}
                    className={`relative p-1.5 sm:p-2 rounded-xl transition-colors ${isDark ? 'text-text-muted hover:text-white hover:bg-dark-800' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 min-w-[16px] sm:min-w-[18px] h-[16px] sm:h-[18px] px-1 bg-accent-red rounded-full flex items-center justify-center text-[9px] sm:text-[10px] font-bold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Panel */}
                  <AnimatePresence>
                    {isNotificationOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className={`fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-full sm:mt-2 w-auto sm:w-80 md:w-96 border rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden z-50 ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200'}`}
                      >
                        {/* Header */}
                        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                          <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Notifications</h3>
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-xs text-accent-purple hover:text-accent-purple-light transition-colors"
                            >
                              Mark all as read
                            </button>
                          )}
                        </div>

                        {/* Notification List */}
                        <div className="max-h-96 overflow-y-auto">
                          {notifications.length === 0 ? (
                            <div className="py-12 text-center">
                              <Bell className={`w-12 h-12 mx-auto mb-3 opacity-50 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                              <p className={isDark ? 'text-text-muted' : 'text-slate-500'}>No notifications yet</p>
                            </div>
                          ) : (
                            notifications.map((notification) => (
                              <div
                                key={notification.id}
                                className={`px-4 py-3 border-b last:border-0 transition-colors cursor-pointer ${isDark ? 'border-border hover:bg-dark-700/50' : 'border-slate-100 hover:bg-slate-50'} ${
                                  !notification.read ? (isDark ? 'bg-dark-700/30' : 'bg-slate-50') : ''
                                }`}
                                onClick={() => handleNotificationClick(notification)}
                              >
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0 mt-0.5">
                                    <NotificationIcon type={notification.type} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                      <p className={`text-sm font-medium ${!notification.read ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-text-secondary' : 'text-slate-600')}`}>
                                        {notification.title}
                                      </p>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeNotification(notification.id);
                                        }}
                                        className={`ml-2 p-1 hover:text-accent-red transition-colors ${isDark ? 'text-text-muted' : 'text-slate-400'}`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                    <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                                      {notification.message}
                                    </p>
                                    <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                                      {formatRelativeTime(notification.created_at)}
                                    </p>
                                  </div>
                                  {!notification.read && (
                                    <div className="flex-shrink-0">
                                      <span className="w-2 h-2 bg-accent-purple rounded-full block" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Footer */}
                        {notifications.length > 0 && (
                          <div className={`px-4 py-2 border-t ${isDark ? 'border-border bg-dark-900/50' : 'border-slate-200 bg-slate-50'}`}>
                            <button
                              onClick={() => {
                                useNotificationStore.getState().clearAll();
                              }}
                              className={`text-xs hover:text-accent-red transition-colors flex items-center space-x-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>Clear all notifications</span>
                            </button>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Mobile User Avatar */}
                <button className="lg:hidden p-1">
                  <div className="w-8 h-8 rounded-full bg-gradient-accent flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </header>
        )}

        {/* Page Content */}
        <main className={(isEmployeeChatPage || isFullWidthPage) ? 'p-0' : 'p-3 sm:p-4 lg:p-8'}>
          <Outlet />
        </main>
      </div>

      {/* New Workspace Modal */}
      <AnimatePresence>
        {isNewWorkspaceModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => setIsNewWorkspaceModalOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-dark-800' : 'bg-white'}`}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  New Workspace
                </h2>
                <button
                  onClick={() => setIsNewWorkspaceModalOpen(false)}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-slate-100 text-slate-400'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                {/* Workspace Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Workspace Name
                  </label>
                  <input
                    type="text"
                    value={newWorkspaceName}
                    onChange={(e) => setNewWorkspaceName(e.target.value)}
                    placeholder="Enter workspace name"
                    className={`w-full px-4 py-3 rounded-xl border transition-colors ${
                      isDark
                        ? 'bg-dark-700 border-border text-white placeholder-text-muted focus:border-accent-purple'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-500'
                    } focus:outline-none focus:ring-2 focus:ring-opacity-20 ${isDark ? 'focus:ring-accent-purple' : 'focus:ring-blue-500'}`}
                  />
                </div>

                {/* Color Picker */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Color
                  </label>
                  <div className="flex items-center gap-3">
                    {Object.entries(workspaceColors).map(([colorName, colorValue]) => (
                      <button
                        key={colorName}
                        onClick={() => setNewWorkspaceColor(colorName)}
                        className={`w-10 h-10 rounded-full transition-transform hover:scale-110 ${
                          newWorkspaceColor === colorName ? 'ring-2 ring-offset-2 ring-offset-dark-800 ring-white scale-110' : ''
                        }`}
                        style={{ backgroundColor: colorValue }}
                      >
                        {newWorkspaceColor === colorName && (
                          <Check className="w-5 h-5 text-white mx-auto" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Workspace Type */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Type
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Team Workspace */}
                    <button
                      onClick={() => setNewWorkspaceType('team')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        newWorkspaceType === 'team'
                          ? isDark
                            ? 'border-accent-purple bg-accent-purple/10'
                            : 'border-blue-500 bg-blue-50'
                          : isDark
                            ? 'border-border hover:border-dark-600 bg-dark-700'
                            : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3 ${
                        newWorkspaceType === 'team'
                          ? 'bg-accent-purple'
                          : isDark ? 'bg-dark-600' : 'bg-slate-200'
                      }`}>
                        <Users className={`w-5 h-5 ${newWorkspaceType === 'team' ? 'text-white' : isDark ? 'text-text-muted' : 'text-slate-500'}`} />
                      </div>
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Team Workspace
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        Collaborate with others
                      </p>
                    </button>

                    {/* Personal Workspace */}
                    <button
                      onClick={() => setNewWorkspaceType('personal')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        newWorkspaceType === 'personal'
                          ? isDark
                            ? 'border-accent-purple bg-accent-purple/10'
                            : 'border-blue-500 bg-blue-50'
                          : isDark
                            ? 'border-border hover:border-dark-600 bg-dark-700'
                            : 'border-slate-200 hover:border-slate-300 bg-slate-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3 ${
                        newWorkspaceType === 'personal'
                          ? 'bg-accent-purple'
                          : isDark ? 'bg-dark-600' : 'bg-slate-200'
                      }`}>
                        <Sparkles className={`w-5 h-5 ${newWorkspaceType === 'personal' ? 'text-white' : isDark ? 'text-text-muted' : 'text-slate-500'}`} />
                      </div>
                      <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Personal Workspace
                      </p>
                      <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        Just for you
                      </p>
                    </button>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className={`px-6 py-4 border-t ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <button
                  onClick={handleCreateWorkspace}
                  disabled={!newWorkspaceName.trim()}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    newWorkspaceName.trim()
                      ? 'bg-gradient-accent text-white hover:shadow-lg hover:shadow-accent-purple/25'
                      : isDark
                        ? 'bg-dark-700 text-text-muted cursor-not-allowed'
                        : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inbox Sidebar Panel */}
      <InboxSidebarPanel />

      {/* Workspace Settings Modal */}
      <WorkspaceSettingsModal
        isOpen={isWorkspaceSettingsOpen}
        onClose={() => {
          setIsWorkspaceSettingsOpen(false);
          setSettingsWorkspace(null);
        }}
        workspace={settingsWorkspace}
      />
    </div>
  );
}
